import { ColumnDef, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, RowSelectionState, useReactTable } from "@tanstack/react-table";
import styles from "./QuestionsTable.module.scss";
import TableCheckbox from "../TableCheckbox/TableCheckbox";
import { useEffect, useMemo, useRef, useState } from "react";
import { QAS } from "@/constants/accessRights";
import { patchQuestionsList } from "@/api";
import { useAppSelector } from "@/store/hooks";
import { selectFilter } from "@/store/qas/qasSlice";
import { selectCategoriesList } from "@/store/qas/qasCategorySlice";
import TableFooter from "../TableFooter/TableFooter";
import { convertDate } from "@/helpers";
import { COLUMNS_LIST } from "./QuestionsTable.const";
import { IAnswer, IQuestionItem } from "@/types/qas/qasQuestionTypes";
import Table from "../Table/Table";
import { IQuestionsTable, TTableDataRow } from "./QuestionsTable.props";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import approveQuestion from "@/api/questions/approveQuestion";
import { useAccessRight, useTranslate } from "@/hooks";
import { useDeleteQuestionsListMutation } from "@/store/RTKQuery/qasSlice";
import { BlockWithError, BlockWithInfo, BlockWithLoading, CustomButton } from "@/ui";
import NoticeSingleAction from "@/ui/NoticeSingleAction/NoticeSingleAction";
import { selectCurrentUserInfo } from "@/store/userSlice";

const QuestionsTable = ({ questionsList }: IQuestionsTable) => {
	const isAccess = useAccessRight();
	const translate = useTranslate();

	const elementRef = useRef<HTMLDivElement>(null);

	const [tableData, setTableData] = useState<TTableDataRow[]>([]); // данные для таблицы
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({}); // состояние выбора строк
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const categoriesList = useAppSelector(selectCategoriesList); // список категорий вопросов
	const [sorting, setSorting] = useState<any[]>([]); // состояние сортировки
	const [changedQuestions, setChangedQuestions] = useState<Record<string, Record<string, string>>[]>([]); // список измененных вопросов
	const [newData, setNewData] = useState<Partial<IQuestionItem>[]>(); // данные для отправки на сервер
	const [showNotification, setShowNotification] = useState<boolean>(false); // показ снейкбара
	const [snackbarMessage, setSnackbarMessage] = useState(""); //сообщение на снейкбаре
	const editableColumns = ["question", "answers", "category"]; // редактируемые колонки
	// преобразование списка категорий в объект для быстрого доступа по имени
	const categoriesNameToId = useMemo<Record<string, string>>(
		() => categoriesList.data.reduce((acc, el) => ({ ...acc, [el.name]: el.id }), {} as Record<string, string>),
		[]
	);
	const [deleteQuestions] = useDeleteQuestionsListMutation();
	const currentUserInfo = useAppSelector(selectCurrentUserInfo); // store - информация о текущем пользователе
	const filter = useAppSelector(selectFilter); // store - фильтр

	useEffect(() => {
		handleRequestTableData();
	}, [currentUserInfo.dataset]);

	const columns = useMemo<ColumnDef<TTableDataRow>[]>(
		() => [
			{
				id: "select",
				header: ({ table }) => (
					<TableCheckbox
						{...{
							checked: table.getIsAllRowsSelected(),
							indeterminate: table.getIsSomeRowsSelected(),
							onChange: table.getToggleAllRowsSelectedHandler(),
							disabled: !isAccess(QAS.QUESTION_APPROVE),
							tabIndex: -1,
							className: styles.checkbox,
						}}
					/>
				),
				cell: ({ row }) => (
					<TableCheckbox
						{...{
							checked: row.getIsSelected(),
							disabled: !row.getCanSelect(),
							indeterminate: row.getIsSomeSelected(),
							onChange: row.getToggleSelectedHandler(),
							tabIndex: -1,
							className: styles.checkbox,
						}}
					/>
				),
				minSize: 55,
				maxSize: 55,
				size: 55,
			},
			...COLUMNS_LIST.map((el) => ({
				...el,
				header: translate(el.header),
				filterFn: "myCustomFilter" as any,
			})),
		],
		[tableContainerRef.current?.clientWidth]
	);

	const defaultColumn: Partial<ColumnDef<any, any>> = {
		maxSize: 400, // максимальная ширина
		enableResizing: true, // включение возможности ресайза
	};

	const table = useReactTable<TTableDataRow>({
		data: tableData, // данные для таблицы
		columns,
		initialState: {
			pagination: {
				pageIndex: 0,
				pageSize: 100,
			},
		},
		state: {
			rowSelection, // состояние выбора строк
			sorting,
		},
		filterFns: {
			// кастомный фильтр
			myCustomFilter: (rows, columnId, filterValue: string) => {
				if (columnId === "answers") return rows.original.answers.toLowerCase().includes(filterValue.toLowerCase());
				else if (columnId === "question") return rows.original.question.toLowerCase().includes(filterValue.toLowerCase());
				else if (columnId === "created") return rows.original.created.toLowerCase().includes(filterValue.toLowerCase());
				else if (columnId === "category") {
					if (filterValue === translate("selectItem_notAssigned")) return rows.original.category.toLowerCase().includes("-"); // для не присвоенных к категориям вопросов
					else return rows.original.category.toLowerCase().includes(filterValue.toLowerCase());
				}
			},
		},
		defaultColumn, // для редактирования
		enableRowSelection: true,
		enableColumnResizing: false, // изменение ширины столбца
		onRowSelectionChange: setRowSelection, // checkbox
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(), // для фильтрации
		getPaginationRowModel: getPaginationRowModel(), // для нумерации страниц
		columnResizeMode: "onChange", // изменение ширины столбца в runtime
		onSortingChange: setSorting, // для сортировки
		getSortedRowModel: getSortedRowModel(),
	});

	const handleRequestTableData = () => {
		if (questionsList.data.questions) {
			const normalizedData = questionsList.data.questions.reduce<{ tableData: TTableDataRow[] }>(
				(acc, item) => {
					const [created, modified] = [convertDate({ timestamp: item._created }), convertDate({ timestamp: item._modified })];
					if (item) {
						acc.tableData.push({
							id: item.id,
							answers: item.answers ? item.answers[0].answer : "-",
							question: item.question,
							category: item.category ? categoriesList.dictionary[item.category] || item.category : `-${item.category}`,
							modified: modified,
							created: created,
							channel: item.answers ? item.answers[0].channel : "-",
						});
					}
					return acc;
				},
				{ tableData: [] }
			);
			setTableData(normalizedData.tableData);
		} else {
			setTableData([]);
		}
	};

	const handleApproveQuestion = (event: React.MouseEvent<HTMLElement>) => {
		// Получаем информацию о целевом элементе
		const target = event.target as HTMLElement;

		// Проверяем, является ли целевой элемент нужным
		if (target.closest("tr")) {
			const trId = target.closest("tr")?.id;
			if (trId !== undefined) {
				const question = tableData.find((el) => el.id === trId);

				const approvedQuestion: Partial<IQuestionItem> = {
					id: question?.id,
					category: question?.category ? categoriesNameToId[question.category] ?? "-" : "-",
					question: question?.question ?? "",
					answers: [{ answer: question?.answers || "", checked: true, channel: question?.channel || "0" }],
				};

				if (approvedQuestion) {
					setSnackbarMessage(translate("title_processing"));
					setShowNotification(true);
					approveQuestion(approvedQuestion)
						.then(() => {
							handleRequestTableData();
							setChangedQuestions([]);
							const newTableData = tableData.filter((row) => ![question?.id].includes(row.id));
							// Обновляем данные таблицы после удаления
							setTableData(newTableData);
						})
						.then(() => {
							setSnackbarMessage(translate("noticeApproval_success"));
						})
						.catch(() => {
							setSnackbarMessage(translate("title_requestError"));
						});
				}
			}
		}
	};

	const handleSubmit = () => {
		const value = newData?.map((el) => ({
			...el,
			...(el.category ? { category: categoriesNameToId[el.category] ?? "-" } : {}),
		}));

		if (value) {
			setShowNotification(true);
			setSnackbarMessage(translate("title_processing"));
			patchQuestionsList(value)
				.then(() => {
					handleRequestTableData();
					setChangedQuestions([]);
				})
				.then(() => {
					setSnackbarMessage(translate("noticeApproval_success"));
				})
				.catch(() => {
					setSnackbarMessage(translate("title_requestError"));
				});
		}
	};

	const handleDeleteRows = async () => {
		if (Object.keys(rowSelection).length === 0) {
			setShowNotification(true);
			setSnackbarMessage(translate("notice_noRowsSelected"));
			return;
		}

		const selectedIds = Object.keys(rowSelection).map((rowIndex) => tableData[Number(rowIndex)].id);
		setShowNotification(true);
		setSnackbarMessage(translate("title_processing"));

		deleteQuestions({
			questionsIdList: selectedIds,
			type: "questions",
		})
			.then(() => {
				const newTableData = tableData.filter((row) => !selectedIds.includes(row.id));
				// Обновляем данные таблицы после удаления
				setTableData(newTableData);
				setRowSelection({}); // Сбрасываем выбор строк
				setSnackbarMessage(translate("noticeApproval_success"));
			})
			.catch(() => {
				setSnackbarMessage(translate("title_requestError"));
			});
	};

	useEffect(() => {
		handleRequestTableData();
	}, [questionsList.data.questions, categoriesList.data, filter]);

	useEffect(() => {
		handleRequestTableData();
	}, []);

	useEffect(() => {
		const selectedList: Partial<IQuestionItem>[] = [];
		Object.keys(rowSelection).forEach((rowNumber) => {
			const answer = tableData[Number(rowNumber)];
			selectedList.push({ id: answer.id, answers: [{ answer: answer.answers, checked: true, channel: answer.channel }] });
		}); // пишем номера строк

		const changedList = changedQuestions.map((el) => {
			const id = Object.keys(el)[0];
			const foundRow = tableData.find((el) => el.id === id);
			const { channel, checked } =
				foundRow && typeof foundRow.answers === "object" && foundRow.answers !== null ? (foundRow.answers as IAnswer) : { channel: "0", checked: false };
			const patchedQuestion = {
				id: id,
				question: el[id].question ?? foundRow?.question,
				category: el[id].category ?? foundRow?.category,
				answers: [{ answer: el[id].answers, channel, checked }],
			};
			return patchedQuestion;
		});

		const merged = new Map<string, Partial<IQuestionItem>>();

		selectedList.forEach((item) => {
			if (item.id) {
				merged.set(item.id, item);
			}
		});

		changedList.forEach((item) => {
			if (item.id) {
				if (merged.has(item.id)) {
					const value = merged.get(item.id);
					if (value) {
						const newQuestionValue = {
							...value,
							question: item.question ?? value.question,
							category: item.category ?? value.category,
							answers: [{ ...(value.answers?.[0] ?? { channel: "0", checked: true, answer: "" }), answer: item.answers[0].answer }],
						};
						merged.set(item.id, newQuestionValue);
					}
				} else {
					merged.set(item.id, item);
				}
			}
		});

		const patchList = Array.from(merged.values());

		setNewData(patchList);
	}, [rowSelection, JSON.stringify(changedQuestions)]);

	useEffect(() => {
		const patchedTableData = tableData.map((el) => {
			const changedQuestion = changedQuestions.find((question) => question.hasOwnProperty(el.id));

			if (changedQuestion) {
				return {
					...el,
					question: changedQuestion[el.id].question ?? el.question,
					answers: changedQuestion[el.id].answers ?? el.answers,
					category: changedQuestion[el.id].category ?? el.category,
				};
			} else {
				return el;
			}
		});

		if (patchedTableData.length !== 0) {
			setTableData(patchedTableData);
		}
	}, [JSON.stringify(changedQuestions)]);

	return (
		<>
			<div className={styles.wrapper} ref={elementRef}>
				{/* <DateRange inputDates={inputStartDate} setInputDates={setInputStartDate} /> */}
				{questionsList.isLoading && <BlockWithLoading />}
				{questionsList.isError && <BlockWithError updateHandler={questionsList.refetchNewQuestions} />}
				{!questionsList.isError &&
					!questionsList.isLoading &&
					(tableData.length !== 0 ? (
						<>
							<div className={styles.container} ref={tableContainerRef}>
								<Table<TTableDataRow>
									table={table}
									tableContainerRef={tableContainerRef}
									completeHeaderOptions={null} // здесь не нужно, т.к. нет фильтров
									editableColumns={editableColumns}
									rowSelection={rowSelection}
									changedCellList={changedQuestions}
									setChangedQuestions={setChangedQuestions}
									handleSubmit={handleSubmit}
									autocompleteCellOptions={{ category: categoriesList.data.map((el) => el.name) }} // для автокомплита ячеек категорий
									withFilters={false}
								>
									<CustomButton variant="text" onClick={handleApproveQuestion} sx={{ width: 17.5, height: 17.5, p: 0, minWidth: 0, ml: "4px", overflow: "initial" }}>
										<FontAwesomeIcon icon={faCircleCheck} className={cn(styles["approve-icon"])} size="xl" title={translate("button_approve")} />
									</CustomButton>
								</Table>
							</div>
							{tableData.length !== 0 && (
								<TableFooter<TTableDataRow>
									table={table}
									rowSelection={rowSelection}
									isHaveChanges={changedQuestions.length !== 0}
									tableContainerRef={tableContainerRef}
									handleSubmit={handleSubmit}
									handleDeleteRows={handleDeleteRows}
									submitMode={true}
								/>
							)}
						</>
					) : (
						<BlockWithInfo title={translate("title_noData")} />
					))}
			</div>
			<NoticeSingleAction
				severity={
					snackbarMessage.toLowerCase().includes("fail") || snackbarMessage.toLowerCase().includes("ошибка") || snackbarMessage.toLowerCase().includes("error")
						? "error"
						: "info"
				}
				showNotification={showNotification}
				setShowNotification={setShowNotification}
				title={snackbarMessage}
			/>
		</>
	);
};

export default QuestionsTable;
